import { Colors } from "@jsq/ds/styles";
import { type ExtensionFieldEnumObjectType } from "~/graphql/supergraphTypes";
import { CELL_EDITOR_STYLES, handleCellEditorKeyboard } from "./utils/cellEditorStyles";
import type { CellEditorParams, ExtensionFieldCellEditor } from "./types";

/**
 * Enumeration Cell Editor for AG Grid
 * Provides dropdown selection for enumeration extension fields
 * Uses design system tokens for consistent styling
 */
export class EnumerationCellEditor implements ExtensionFieldCellEditor {
  private eGui!: HTMLSelectElement;
  private params!: CellEditorParams;
  private currentValue: string = "";
  private initialValue: string = "";

  constructor() {
    // Constructor intentionally empty
  }

  init(params: CellEditorParams): void {
    this.params = params;

    // Create the select element
    this.eGui = document.createElement("select");

    // Apply consistent styling using design system tokens
    Object.assign(this.eGui.style, {
      ...CELL_EDITOR_STYLES.container,
      border: `1px solid ${Colors.Neutral300}`,
      borderRadius: "4px",
      padding: "4px 8px",
      fontSize: "14px",
      outline: "none",
      cursor: "pointer",
      backgroundColor: Colors.Neutral0,
      boxSizing: "border-box",
    });

    // Extract initial value from the extension field value object
    const extensionFieldValue = params.value;
    this.initialValue = extensionFieldValue?.enumValue?.enumValueCode || "";
    this.currentValue = this.initialValue;

    // Populate the select with enum options FIRST
    this.populateSelectOptions();

    // Set the initial value AFTER populating options
    this.eGui.value = this.currentValue;

    // Add change event listener
    this.eGui.addEventListener("change", (event) => {
      const target = event.target as HTMLSelectElement;
      this.currentValue = target.value;
    });

    // Add keyboard event listener using shared utility
    this.eGui.addEventListener("keydown", (event) => {
      handleCellEditorKeyboard(event, {
        inputElement: this.eGui,
        initialValue: this.initialValue,
        currentValueSetter: (value) => {
          this.currentValue = value;
        },
        api: this.params.api,
      });
    });
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  getValue(): string {
    return this.currentValue;
  }

  destroy(): void {
    // Cleanup if needed
  }

  isPopup(): boolean {
    return false;
  }

  isCancelBeforeStart(): boolean {
    return false;
  }

  isCancelAfterEnd(): boolean {
    return false;
  }

  afterGuiAttached(): void {
    if (this.eGui) {
      // Focus and open the dropdown
      this.eGui.focus();
      // Small delay to ensure the dropdown opens properly
      setTimeout(() => {
        if (this.eGui && document.activeElement === this.eGui) {
          // Trigger click to open dropdown on some browsers
          this.eGui.click();
        }
      }, 10);
    }
  }

  /**
   * Get enum values from the cell editor parameters using consistent DataVault pattern
   */
  private getEnumValues(): ExtensionFieldEnumObjectType[] {
    // Priority: cellEditorParams.enumValues -> field.activeEnumValues -> field.enumValues
    let enumValues =
      this.params.enumValues ||
      this.params.field?.activeEnumValues ||
      this.params.field?.enumValues ||
      [];

    // Ensure enumValues is an array
    if (!Array.isArray(enumValues)) {
      enumValues = [];
    }

    // If no enum values found, try to extract from the current value's enum structure
    if (enumValues.length === 0 && this.params.value?.enumValue) {
      const currentEnumValue = this.params.value.enumValue;
      if (currentEnumValue.enumValueCode && currentEnumValue.enumValueLabel) {
        enumValues = [currentEnumValue];
      }
    }

    return enumValues;
  }

  /**
   * Create and append placeholder option to the select element
   */
  private addPlaceholderOption(): void {
    const placeholderOption = document.createElement("option");
    placeholderOption.value = "";
    placeholderOption.textContent = "Select an option";
    placeholderOption.style.color = Colors.Neutral500;
    placeholderOption.style.fontStyle = "italic";
    this.eGui.appendChild(placeholderOption);
  }

  /**
   * Create and append enum value options to the select element
   */
  private addEnumValueOptions(enumValues: ExtensionFieldEnumObjectType[]): void {
    enumValues.forEach((enumValue) => {
      if (enumValue?.enumValueCode && enumValue?.enumValueLabel) {
        const option = document.createElement("option");
        option.value = enumValue.enumValueCode;
        option.textContent = enumValue.enumValueLabel;
        this.eGui.appendChild(option);
      }
    });
  }

  /**
   * Create and append error message option when no enum values are available
   */
  private addErrorMessageOption(): void {
    const messageOption = document.createElement("option");
    messageOption.value = "";
    messageOption.textContent = "No enum values configured";
    messageOption.disabled = true;
    messageOption.style.color = Colors.Danger600;
    messageOption.style.fontStyle = "italic";
    this.eGui.appendChild(messageOption);
  }

  /**
   * Populate the select element with enum options
   */
  private populateSelectOptions(): void {
    // Clear existing options
    this.eGui.innerHTML = "";

    const enumValues = this.getEnumValues();

    // Add placeholder option
    this.addPlaceholderOption();

    // Add enum value options or error message
    if (enumValues.length > 0) {
      this.addEnumValueOptions(enumValues);
    } else {
      this.addErrorMessageOption();
    }
  }
}
